import { useAuth0 } from "@auth0/auth0-react";
import { authProviderType } from "@/providers/AuthenticationProvider";
import { clearAssumedUserStorage } from "@/providers/AssumeUserProvider/storage";
import { resolveAuthenticatedUserSub } from "@/lib/authenticatedUserSub";
import { useOktaAuth } from "@okta/okta-react";
import { JwtPayload, jwtDecode } from "jwt-decode";

export type CurrentUser = {
  sub: string;
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  department: string;
  location: string;
  organization: string;
  timezone?: string;
  accessTokenClaims: () => Promise<{
    backoffice_roles: string[] | undefined;
    backoffice_sections: string[] | undefined;
  }>;
};

interface Auth0AccessTokenClaims extends JwtPayload {
  backoffice_sections: string[] | undefined;
  backoffice_roles: string[] | undefined;
  zoneinfo?: string;
}

type ICurrentUser = { user: CurrentUser; getAccessToken: () => Promise<string>; logout: () => Promise<boolean> };

export function deriveGivenName(givenName?: string | null, fullName?: string | null): string {
  const trimmedGivenName = givenName?.trim();
  if (trimmedGivenName) {
    return trimmedGivenName;
  }

  return fullName?.trim().split(/\s+/)[0] ?? "";
}

export function resolveWorkdayDepartment(claims: Record<string, unknown> | null | undefined): string {
  const workday = claims?.wddepartment;
  if (typeof workday === "string" && workday !== "") {
    return workday;
  }

  const legacy = claims?.department;
  return typeof legacy === "string" ? legacy : "";
}

const useCurrentUser: () => ICurrentUser = (() => {
  if (authProviderType === "token") {
    const token = import.meta.env.VITE_API_ACCESS_TOKEN as string;
    if (!token) {
      throw new Error(
        "VITE_API_ACCESS_TOKEN is required when VITE_AUTH_PROVIDER=token. " +
          "Run ./scripts/setup-token-auth-provider.sh first."
      );
    }
    const decoded = jwtDecode<Auth0AccessTokenClaims & Record<string, unknown>>(token);
    function useTokenCurrentUser() {
      const authenticatedUserSub = resolveAuthenticatedUserSub(decoded);

      return {
        user: {
          sub: authenticatedUserSub,
          email: (decoded.email as string) ?? "token@local",
          name: (decoded.name as string) ?? "Token User",
          given_name:
            deriveGivenName(decoded.given_name as string | undefined, decoded.name as string | undefined) || "Token",
          family_name: (decoded.family_name as string) ?? "User",
          picture: "",
          department: resolveWorkdayDepartment(decoded),
          location: "",
          organization: "",
          timezone: typeof decoded.zoneinfo === "string" ? decoded.zoneinfo : undefined,
          accessTokenClaims: async () => ({
            backoffice_roles: decoded.backoffice_roles,
            backoffice_sections: decoded.backoffice_sections
          })
        },
        getAccessToken: async () => token,
        logout: async () => {
          clearAssumedUserStorage();
          return true;
        }
      };
    }
    return useTokenCurrentUser;
  }

  if (authProviderType === "okta") {
    function useOktaCurrentUser() {
      const { authState, oktaAuth } = useOktaAuth();

      return {
        user: {
          sub: resolveAuthenticatedUserSub(authState!.idToken!.claims as Record<string, unknown>),
          email: authState!.idToken!.claims.email!,
          name: authState!.idToken!.claims.name!,
          given_name: deriveGivenName(
            authState!.idToken!.claims.given_name?.toString(),
            authState!.idToken!.claims.name?.toString()
          ),
          family_name: authState!.idToken!.claims.family_name?.toString() || "",
          picture: authState!.idToken!.claims.avatar?.toString() || "",
          department: resolveWorkdayDepartment(authState!.idToken!.claims as Record<string, unknown>),
          location: authState!.idToken!.claims.location?.toString() || "",
          organization: authState!.idToken!.claims.organization?.toString() || "",
          timezone: authState!.idToken!.claims.zoneinfo?.toString(),
          accessTokenClaims: async () => {
            const claims = authState!.accessToken!.claims!;
            return {
              backoffice_roles: claims.backoffice_roles as string[],
              backoffice_sections: claims.backoffice_sections as string[]
            };
          }
        },
        getAccessToken: async () => (await oktaAuth.getOrRenewAccessToken())!,
        logout: async () => {
          clearAssumedUserStorage();
          await oktaAuth.signOut();
          return true;
        }
      };
    }
    return useOktaCurrentUser;
  }

  function useAuth0CurrentUser() {
    const { user, getAccessTokenSilently, logout } = useAuth0();

    return {
      user: {
        sub: resolveAuthenticatedUserSub(user as Record<string, unknown>),
        email: user!.email!,
        name: user!.name!,
        given_name: deriveGivenName(user!.given_name, user!.name),
        family_name: user!.family_name!,
        picture: user!.picture!,
        department: resolveWorkdayDepartment(user as Record<string, unknown>),
        location: user!.location! as string,
        organization: user!.organization! as string,
        timezone: (user as { zoneinfo?: string }).zoneinfo,
        accessTokenClaims: async () => {
          const accessToken = await getAccessTokenSilently();
          const claims = jwtDecode<Auth0AccessTokenClaims>(accessToken);
          return {
            backoffice_roles: claims.backoffice_roles as string[],
            backoffice_sections: claims.backoffice_sections as string[]
          };
        }
      },
      getAccessToken: getAccessTokenSilently,
      logout: async () => {
        clearAssumedUserStorage();
        await logout({ logoutParams: { returnTo: window.location.origin } });
        return true;
      }
    };
  }
  return useAuth0CurrentUser;
})();

export default useCurrentUser;
